import { useState } from "react";
import { useLocation, Link } from "react-router-dom";
import { API } from "@/App";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { ScrollArea } from "@/components/ui/scroll-area";
import { toast } from "sonner";
import {
  GitCompare,
  Loader2,
  Trophy,
  ArrowLeft,
  TrendingUp,
  ThumbsUp,
  ArrowRight,
  CheckCircle,
  Minus
} from "lucide-react";

export default function ScriptCompare() {
  const location = useLocation();
  const originalFromState = location.state?.originalScript || "";
  const scriptTypeFromState = location.state?.scriptType || "cold_call";

  const [originalScript, setOriginalScript] = useState(originalFromState);
  const [newScript, setNewScript] = useState("");
  const [comparing, setComparing] = useState(false);
  const [result, setResult] = useState(null);

  const handleCompare = async () => {
    if (!originalScript.trim() || !newScript.trim()) {
      toast.error("Both scripts are required");
      return;
    }
    setComparing(true);
    setResult(null);
    try {
      const response = await fetch(`${API}/scripts/compare`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          original_script: originalScript,
          new_script: newScript,
          script_type: scriptTypeFromState
        })
      });
      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err.detail || "Comparison failed");
      }
      const data = await response.json();
      setResult(data.comparison);
      toast.success("Comparison complete!");
    } catch (error) {
      toast.error("Failed to compare scripts", { description: error.message });
    } finally {
      setComparing(false);
    }
  };

  const getScoreColor = (score) => {
    if (score >= 80) return "text-green-500";
    if (score >= 60) return "text-yellow-500";
    return "text-red-500";
  };

  const getScoreBg = (score) => {
    if (score >= 80) return "bg-green-500";
    if (score >= 60) return "bg-yellow-500";
    return "bg-red-500";
  };

  return (
    <div className="max-w-6xl mx-auto space-y-8" data-testid="script-compare-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl font-bold">Compare Scripts</h1>
          <p className="text-muted-foreground mt-1">
            See how your new version stacks up against the original
          </p>
        </div>
        <Link to="/scripts/history">
          <Button variant="outline">
            <ArrowLeft className="w-4 h-4 mr-2" /> Back to History
          </Button>
        </Link>
      </div>

      {/* Input Area - Side by Side */}
      <div className="grid md:grid-cols-2 gap-6">
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-lg flex items-center gap-2">
              <Badge variant="outline" className="font-mono">A</Badge>
              Original Script
            </CardTitle>
          </CardHeader>
          <CardContent>
            <Textarea
              placeholder="Paste original script here..."
              value={originalScript}
              onChange={(e) => setOriginalScript(e.target.value)}
              rows={12}
              className="font-mono text-sm"
              data-testid="original-script-input"
            />
            <p className="text-xs text-muted-foreground text-right mt-1">
              {originalScript.length} chars
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-lg flex items-center gap-2">
              <Badge variant="outline" className="font-mono">B</Badge>
              New Version
            </CardTitle>
          </CardHeader>
          <CardContent>
            <Textarea
              placeholder="Paste your improved script here..."
              value={newScript}
              onChange={(e) => setNewScript(e.target.value)}
              rows={12}
              className="font-mono text-sm"
              data-testid="new-script-input"
            />
            <p className="text-xs text-muted-foreground text-right mt-1">
              {newScript.length} chars
            </p>
          </CardContent>
        </Card>
      </div>

      <div className="flex justify-center">
        <Button
          onClick={handleCompare}
          disabled={!originalScript.trim() || !newScript.trim() || comparing}
          size="lg"
          data-testid="compare-btn"
        >
          {comparing ? (
            <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Comparing Scripts...</>
          ) : (
            <><GitCompare className="w-4 h-4 mr-2" /> Compare Scripts</>
          )}
        </Button>
      </div>

      {/* Comparison Results */}
      {result && (
        <div className="space-y-6" data-testid="compare-results">
          {/* Winner Banner */}
          <Card className="border-2 border-primary/30 bg-primary/5">
            <CardContent className="p-6 text-center">
              <Trophy className="w-10 h-10 text-primary mx-auto mb-3" />
              <h2 className="font-heading text-xl font-bold mb-2">
                {result.winner === "original" ? "Original Script Wins" :
                 result.winner === "new" ? "New Version Wins" : "It's a Tie"}
              </h2>
              <p className="text-muted-foreground max-w-2xl mx-auto">{result.summary}</p>
            </CardContent>
          </Card>

          {/* Score Comparison */}
          <div className="grid md:grid-cols-2 gap-6">
            <Card className={`relative overflow-hidden ${result.winner === "original" ? "ring-2 ring-primary" : ""}`}
                  data-testid="original-score-card">
              <div className={`absolute top-0 left-0 right-0 h-1 ${getScoreBg(result.original_score)}`} />
              <CardContent className="p-6 text-center">
                {result.winner === "original" && (
                  <Badge className="mb-2 bg-primary/10 text-primary">Winner</Badge>
                )}
                <Label className="text-xs text-muted-foreground">Original Script</Label>
                <p className={`font-heading text-5xl font-bold ${getScoreColor(result.original_score)}`}>
                  {result.original_score}
                </p>
              </CardContent>
            </Card>

            <Card className={`relative overflow-hidden ${result.winner === "new" ? "ring-2 ring-primary" : ""}`}
                  data-testid="new-score-card">
              <div className={`absolute top-0 left-0 right-0 h-1 ${getScoreBg(result.new_score)}`} />
              <CardContent className="p-6 text-center">
                {result.winner === "new" && (
                  <Badge className="mb-2 bg-primary/10 text-primary">Winner</Badge>
                )}
                <Label className="text-xs text-muted-foreground">New Version</Label>
                <p className={`font-heading text-5xl font-bold ${getScoreColor(result.new_score)}`}>
                  {result.new_score}
                </p>
              </CardContent>
            </Card>
          </div>

          {/* Metrics Comparison */}
          {result.metrics_comparison && Object.keys(result.metrics_comparison).length > 0 && (
            <Card data-testid="metrics-comparison-card">
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <TrendingUp className="w-5 h-5 text-primary" /> Metric Comparison
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-5">
                  {Object.entries(result.metrics_comparison).map(([key, values]) => (
                    <div key={key} className="space-y-2">
                      <div className="flex justify-between items-center">
                        <span className="text-sm font-medium capitalize">{key.replace(/_/g, " ")}</span>
                        <div className="flex items-center gap-3 text-sm font-mono">
                          <span className={getScoreColor(values.original)}>{values.original}</span>
                          <span className="text-muted-foreground">vs</span>
                          <span className={getScoreColor(values.new)}>{values.new}</span>
                        </div>
                      </div>
                      <div className="flex gap-1 h-2">
                        <div className="flex-1 rounded-l-full bg-muted overflow-hidden">
                          <div
                            className={`h-full rounded-l-full ${values.original >= values.new ? "bg-primary" : "bg-muted-foreground/30"}`}
                            style={{ width: `${values.original}%` }}
                          />
                        </div>
                        <div className="flex-1 rounded-r-full bg-muted overflow-hidden">
                          <div
                            className={`h-full rounded-r-full ${values.new >= values.original ? "bg-primary" : "bg-muted-foreground/30"}`}
                            style={{ width: `${values.new}%` }}
                          />
                        </div>
                      </div>
                      <div className="flex justify-between text-xs text-muted-foreground">
                        <span>Original</span>
                        <span>New</span>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Strengths Side by Side */}
          <div className="grid md:grid-cols-2 gap-6">
            {result.original_strengths?.length > 0 && (
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-lg flex items-center gap-2 text-green-500">
                    <ThumbsUp className="w-5 h-5" /> Original Strengths
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-2">
                    {result.original_strengths.map((s, i) => (
                      <li key={i} className="flex items-start gap-2">
                        <div className="w-2 h-2 rounded-full bg-green-500 mt-2 flex-shrink-0" />
                        <span className="text-sm">{s}</span>
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            )}

            {result.new_strengths?.length > 0 && (
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-lg flex items-center gap-2 text-green-500">
                    <ThumbsUp className="w-5 h-5" /> New Version Strengths
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-2">
                    {result.new_strengths.map((s, i) => (
                      <li key={i} className="flex items-start gap-2">
                        <div className="w-2 h-2 rounded-full bg-green-500 mt-2 flex-shrink-0" />
                        <span className="text-sm">{s}</span>
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            )}
          </div>

          {/* Key Differences */}
          {result.key_differences?.length > 0 && (
            <Card data-testid="key-differences-card">
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <GitCompare className="w-5 h-5 text-primary" /> Key Differences
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {result.key_differences.map((diff, i) => (
                  <div key={i} className="p-4 rounded-lg bg-muted/50 space-y-3">
                    <h4 className="font-medium capitalize">{diff.aspect}</h4>
                    <div className="grid md:grid-cols-2 gap-3">
                      <div className="text-sm">
                        <Badge variant="outline" className="mb-1 font-mono text-xs">A</Badge>
                        <p className="text-muted-foreground">{diff.original}</p>
                      </div>
                      <div className="text-sm">
                        <Badge variant="outline" className="mb-1 font-mono text-xs">B</Badge>
                        <p className="text-muted-foreground">{diff.new}</p>
                      </div>
                    </div>
                    <p className="text-sm text-primary font-medium">{diff.verdict}</p>
                  </div>
                ))}
              </CardContent>
            </Card>
          )}

          {/* Recommendations */}
          {result.recommendations?.length > 0 && (
            <Card data-testid="compare-recommendations-card">
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <ArrowRight className="w-5 h-5 text-primary" /> Recommendations
                </CardTitle>
              </CardHeader>
              <CardContent>
                <ul className="space-y-2">
                  {result.recommendations.map((rec, i) => (
                    <li key={i} className="flex items-center gap-3 p-3 rounded-lg bg-primary/5">
                      <span className="w-6 h-6 rounded-full bg-primary text-primary-foreground text-xs font-bold flex items-center justify-center">
                        {i + 1}
                      </span>
                      <span className="text-sm">{rec}</span>
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>
          )}
        </div>
      )}
    </div>
  );
}
